/**
 * /crm/settings/whatsapp — CRM WhatsApp notification configuration.
 */
import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Loader2, MessageCircle, QrCode, Send, ShieldCheck, XCircle } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { ErrorState } from '@/components/ui/ErrorState';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  useLogoutWhatsAppSession,
  usePatchWhatsAppSettings,
  useTestWhatsAppSettings,
  useWhatsAppQr,
  useWhatsAppSettings,
  useWhatsAppStatus,
  type CrmWhatsAppProvider,
  type CrmWhatsAppSettings,
  type CrmWhatsAppSettingsPayload,
} from '@/api/bi/crmSettings';
import { apiErrorMessage } from '@/lib/apiError';
import { notify } from '@/lib/toast';
import { cn } from '@/lib/cn';

interface FormState {
  is_enabled: boolean;
  provider: CrmWhatsAppProvider;
  cloud_phone_number_id: string;
  cloud_waba_id: string;
  cloud_access_token: string;
  cloud_access_token_dirty: boolean;
  template_name_assigned: string;
  template_name_escalated: string;
  template_language: string;
  qr_service_base_url: string;
  qr_service_token: string;
  qr_service_token_dirty: boolean;
}

function fromServer(server: CrmWhatsAppSettings | undefined): FormState {
  return {
    is_enabled: server?.is_enabled ?? false,
    provider: server?.provider ?? 'cloud_api',
    cloud_phone_number_id: server?.cloud_phone_number_id ?? '',
    cloud_waba_id: server?.cloud_waba_id ?? '',
    cloud_access_token: '',
    cloud_access_token_dirty: false,
    template_name_assigned: server?.template_name_assigned ?? 'crm_ticket_assigned',
    template_name_escalated: server?.template_name_escalated ?? 'crm_ticket_escalated',
    template_language: server?.template_language ?? 'en_US',
    qr_service_base_url: server?.qr_service_base_url ?? '',
    qr_service_token: '',
    qr_service_token_dirty: false,
  };
}

function queryErr(err: unknown): string | null {
  if (!err) return null;
  if (err instanceof Error) return err.message;
  return String(err);
}

export function CrmWhatsAppSettingsPage() {
  const settingsQuery = useWhatsAppSettings();
  const patch = usePatchWhatsAppSettings();
  const test = useTestWhatsAppSettings();
  const logout = useLogoutWhatsAppSession();
  const [form, setForm] = useState<FormState>(() => fromServer(undefined));
  const [testPhone, setTestPhone] = useState('');

  /** Saved row: QR Web + non-empty base URL (what Django uses for /status, /qr, /logout). */
  const serverReadyForQr = useMemo(
    () =>
      settingsQuery.data?.provider === 'qr_web' &&
      Boolean(settingsQuery.data?.qr_service_base_url?.trim()),
    [settingsQuery.data?.provider, settingsQuery.data?.qr_service_base_url],
  );
  const qrHooksEnabled = form.provider === 'qr_web';
  const statusQuery = useWhatsAppStatus(qrHooksEnabled, serverReadyForQr);
  const qrQuery = useWhatsAppQr(qrHooksEnabled);

  useEffect(() => {
    if (settingsQuery.data) setForm(fromServer(settingsQuery.data));
  }, [settingsQuery.data]);

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function buildPayload(): CrmWhatsAppSettingsPayload {
    const payload: CrmWhatsAppSettingsPayload = {
      is_enabled: form.is_enabled,
      provider: form.provider,
      cloud_phone_number_id: form.cloud_phone_number_id.trim(),
      cloud_waba_id: form.cloud_waba_id.trim(),
      template_name_assigned: form.template_name_assigned.trim() || 'crm_ticket_assigned',
      template_name_escalated: form.template_name_escalated.trim() || 'crm_ticket_escalated',
      template_language: form.template_language.trim() || 'en_US',
      qr_service_base_url: form.qr_service_base_url.trim(),
    };
    if (form.cloud_access_token_dirty) payload.cloud_access_token = form.cloud_access_token;
    if (form.qr_service_token_dirty) payload.qr_service_token = form.qr_service_token;
    return payload;
  }

  async function handleSave() {
    try {
      await patch.mutateAsync(buildPayload());
      notify.success('WhatsApp settings saved.');
      setForm((f) => ({
        ...f,
        cloud_access_token: '',
        cloud_access_token_dirty: false,
        qr_service_token: '',
        qr_service_token_dirty: false,
      }));
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Could not save WhatsApp settings.'));
    }
  }

  async function handleTest() {
    if (!testPhone.trim()) {
      notify.error('Enter a WhatsApp recipient phone number.');
      return;
    }
    try {
      const result = await test.mutateAsync({ ...buildPayload(), to_phone: testPhone.trim() });
      if (result.ok) {
        notify.success(`Test WhatsApp message sent to ${testPhone.trim()}.`);
      } else {
        notify.error(`WhatsApp test failed: ${result.error ?? 'unknown error'}`);
      }
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Could not test WhatsApp config.'));
    }
  }

  async function handleLogout() {
    if (!serverReadyForQr) {
      notify.warning(
        'Save changes with Integration mode set to QR Web Session and a non-empty QR service URL first. Logout uses the saved server settings, not only what you typed in the form.',
      );
      return;
    }
    try {
      const result = await logout.mutateAsync();
      if (result.error) notify.error(result.error);
      else notify.success('QR WhatsApp session logged out.');
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Could not log out WhatsApp session.'));
    }
  }

  async function handleRefreshQrStatus() {
    if (!serverReadyForQr) {
      notify.warning(
        'Save changes first so the API can reach your QR service URL. The status request always uses the saved settings.',
      );
      return;
    }
    await Promise.all([statusQuery.refetch(), qrQuery.refetch()]);
  }

  const qrValue = qrQuery.data?.qr ?? statusQuery.data?.qr ?? null;
  const connected = Boolean(statusQuery.data?.connected);
  const statusOrQrError =
    statusQuery.data?.error ||
    qrQuery.data?.error ||
    queryErr(statusQuery.error) ||
    queryErr(qrQuery.error);

  return (
    <>
      <PageHeader
        eyebrow="CRM Settings"
        title="WhatsApp"
        description="Send ticket assignment and escalation alerts to staff WhatsApp numbers using Cloud API or a QR-backed Web session."
        actions={
          <Badge tone={form.is_enabled ? 'success' : 'neutral'}>
            <ShieldCheck className="mr-1 h-3 w-3" /> {form.is_enabled ? 'enabled' : 'disabled'}
          </Badge>
        }
      />

      {settingsQuery.isLoading ? (
        <Skeleton className="h-64" />
      ) : settingsQuery.isError ? (
        <ErrorState
          title="Could not load WhatsApp settings"
          description={String((settingsQuery.error as Error)?.message ?? '')}
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-3">
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Provider</CardTitle>
            </CardHeader>
            <CardBody className="space-y-4">
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="Integration mode">
                  <Select
                    value={form.provider}
                    onValueChange={(v) => setField('provider', v as CrmWhatsAppProvider)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cloud_api">Official Cloud API</SelectItem>
                      <SelectItem value="qr_web">QR Web Session</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Template language">
                  <Input
                    value={form.template_language}
                    onChange={(e) => setField('template_language', e.target.value)}
                    placeholder="en_US"
                  />
                </Field>
              </div>

              {form.provider === 'cloud_api' ? (
                <div className="grid gap-3 md:grid-cols-2">
                  <Field label="Phone number ID">
                    <Input
                      value={form.cloud_phone_number_id}
                      onChange={(e) => setField('cloud_phone_number_id', e.target.value)}
                      placeholder="Meta phone number ID"
                    />
                  </Field>
                  <Field label="WABA ID">
                    <Input
                      value={form.cloud_waba_id}
                      onChange={(e) => setField('cloud_waba_id', e.target.value)}
                      placeholder="WhatsApp Business Account ID"
                    />
                  </Field>
                  <Field
                    label="Access token"
                    hint={
                      settingsQuery.data?.has_cloud_access_token
                        ? 'Leave blank to keep the saved token.'
                        : 'Permanent or system-user token with WhatsApp messaging permissions.'
                    }
                  >
                    <Input
                      type="password"
                      value={form.cloud_access_token_dirty ? form.cloud_access_token : ''}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          cloud_access_token: e.target.value,
                          cloud_access_token_dirty: true,
                        }))
                      }
                      placeholder={settingsQuery.data?.has_cloud_access_token ? '•••••••••• (saved)' : ''}
                      autoComplete="new-password"
                    />
                  </Field>
                  <Field label="Assigned template">
                    <Input
                      value={form.template_name_assigned}
                      onChange={(e) => setField('template_name_assigned', e.target.value)}
                      placeholder="crm_ticket_assigned"
                    />
                  </Field>
                  <Field label="Escalated template">
                    <Input
                      value={form.template_name_escalated}
                      onChange={(e) => setField('template_name_escalated', e.target.value)}
                      placeholder="crm_ticket_escalated"
                    />
                  </Field>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning-soft">
                    QR Web Session is unofficial and best suited for internal operational alerts. Use
                    Cloud API for production-critical or policy-sensitive messaging.
                  </p>
                  <div className="grid gap-3 md:grid-cols-2">
                    <Field
                      label="QR service URL"
                      hint="Must be reachable from the Django API (not only your browser). On Docker Desktop for Mac/Windows use http://host.docker.internal:3025 when the Node service runs on the host."
                    >
                      <Input
                        value={form.qr_service_base_url}
                        onChange={(e) => setField('qr_service_base_url', e.target.value)}
                        placeholder="http://host.docker.internal:3025"
                      />
                    </Field>
                    <Field
                      label="Service token"
                      hint={
                        settingsQuery.data?.has_qr_service_token
                          ? 'Leave blank to keep the saved token.'
                          : 'Shared bearer token used between backend and QR service.'
                      }
                    >
                      <Input
                        type="password"
                        value={form.qr_service_token_dirty ? form.qr_service_token : ''}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            qr_service_token: e.target.value,
                            qr_service_token_dirty: true,
                          }))
                        }
                        placeholder={settingsQuery.data?.has_qr_service_token ? '•••••••••• (saved)' : ''}
                      />
                    </Field>
                  </div>
                </div>
              )}

              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={form.is_enabled}
                  onChange={(e) => setField('is_enabled', e.target.checked)}
                />
                Enabled for outbound CRM WhatsApp notifications
              </label>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Test &amp; status</CardTitle>
            </CardHeader>
            <CardBody className="space-y-3 text-sm">
              <div
                className={cn(
                  'rounded-md border px-3 py-2 text-xs',
                  connected
                    ? 'border-success/30 bg-success/10 text-success'
                    : 'border-border bg-surface text-muted-foreground',
                )}
              >
                <div className="flex items-center gap-1.5 font-medium">
                  {connected ? <CheckCircle2 className="h-3.5 w-3.5" /> : <MessageCircle className="h-3.5 w-3.5" />}
                  QR session {connected ? 'connected' : 'not connected'}
                </div>
                {statusOrQrError ? (
                  <p className="mt-1 break-words text-[11px] text-danger">{statusOrQrError}</p>
                ) : null}
              </div>

              {form.provider === 'qr_web' ? (
                <div className="space-y-2">
                  {qrHooksEnabled && settingsQuery.data && !serverReadyForQr ? (
                    <p className="rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning-soft">
                      The server does not have QR Web + a saved service URL yet (or they differ from this form).
                      Click <strong>Save changes</strong>, then use Refresh status.
                    </p>
                  ) : null}
                  {qrValue ? (
                    <div className="rounded-md border border-border bg-white p-2">
                      <img src={qrValue} alt="WhatsApp QR code" className="mx-auto h-48 w-48 object-contain" />
                    </div>
                  ) : (
                    <p className="rounded-md border border-border bg-surface px-3 py-2 text-xs text-muted-foreground">
                      <QrCode className="mr-1 inline h-3.5 w-3.5" />
                      {!serverReadyForQr
                        ? 'After you save QR Web settings and a service URL, refresh to load the code.'
                        : 'No QR image yet. If the backend can reach the service, wait a few seconds and refresh — the session may still be starting.'}
                    </p>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => void handleRefreshQrStatus()}
                  >
                    Refresh status
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => void handleLogout()}
                    disabled={logout.isPending || !serverReadyForQr}
                  >
                    Logout session
                  </Button>
                </div>
              ) : null}

              <Field label="Send test to">
                <div className="flex gap-2">
                  <Input
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    placeholder="9841..."
                  />
                  <Button size="sm" variant="outline" onClick={handleTest} disabled={test.isPending}>
                    {test.isPending ? (
                      <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Send className="mr-1 h-3.5 w-3.5" />
                    )}
                    Test
                  </Button>
                </div>
              </Field>

              {settingsQuery.data?.last_test_at ? (
                <div
                  className={cn(
                    'rounded-md border px-3 py-2 text-xs',
                    settingsQuery.data.last_test_status === 'ok'
                      ? 'border-success/30 bg-success/10 text-success'
                      : 'border-danger/30 bg-danger/10 text-danger',
                  )}
                >
                  <div className="flex items-center gap-1.5 font-medium">
                    {settingsQuery.data.last_test_status === 'ok' ? (
                      <CheckCircle2 className="h-3.5 w-3.5" />
                    ) : (
                      <XCircle className="h-3.5 w-3.5" />
                    )}
                    Last test {new Date(settingsQuery.data.last_test_at).toLocaleString()}
                  </div>
                  {settingsQuery.data.last_error ? (
                    <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-words text-[11px] opacity-80">
                      {settingsQuery.data.last_error}
                    </pre>
                  ) : null}
                </div>
              ) : null}
            </CardBody>
          </Card>

          <div className="flex justify-end gap-2 lg:col-span-3">
            <Button onClick={handleSave} disabled={patch.isPending}>
              {patch.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
              Save changes
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
